import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronDown, Sparkles, Shuffle, X, ArrowRight, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { HelpSheet } from "@/v2/today3/HelpSheet";

const EXPAND = { initial: { height: 0, opacity: 0 }, animate: { height: "auto", opacity: 1 }, exit: { height: 0, opacity: 0 } };
const ROW_IN = { opacity: 0, y: 8 };
const ROW_SHOWN = { opacity: 1, y: 0 };
const ROW_DRAG = { scale: 1.02, boxShadow: "0 12px 30px rgba(45,41,38,0.15)" };
const TAP = { scale: 0.9 };

export async function actMust(m, action) {
  if (action === "drop") return api.post(`/v2/today/must/${encodeURIComponent(m.id)}`, { action: "drop" });
  const done = action === "done";
  if (m.source === "step") return api.post("/v2/today/feedback", { slot: m.slot, action: done ? "done" : "undo" });
  if (m.source === "plan") return api.post(`/v2/plans/${m.plan_id}`, { action: done ? "done" : "reopen" });
  if (m.source === "mission") return api.post(done ? "/v2/missions/tick" : "/v2/missions/untick", {});
  return api.post(`/v2/today/must/${encodeURIComponent(m.id)}`, { action });
}

export function LinkChip({ link, testid }) {
  if (!link) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 max-w-full bg-[#EDF2EE] text-[#4F6B55] text-xs font-medium px-2.5 py-1 rounded-full">
      <ArrowRight className="h-3 w-3 shrink-0" /><span className="truncate">{link.label}</span>
    </span>
  );
}

function RowMenu({ m, n, busy, run, help, move, count }) {
  const canSwap = m.source === "step" && !m.plan_id && m.status !== "done";
  const mv = "h-10 w-10 rounded-xl border border-[#EFECE6] grid place-items-center text-[#635E59] disabled:opacity-30";
  return (
    <motion.div {...EXPAND} className="overflow-hidden">
      {m.why && <p className="text-sm text-[#635E59] mt-3" data-testid={`must-do-why-${n}`}>{m.why}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={help} data-testid={`help-me-do-it-button-${n}`} className="h-10 px-3 rounded-xl border border-[#C86D51]/30 text-[#C86D51] hover:bg-[#F8EFEA] text-xs font-semibold inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" />Help me do it</button>
        {canSwap && <button disabled={busy} onClick={() => run(() => api.post("/v2/today/feedback", { slot: m.slot, action: "swap" }), "Swapped in something different.")} data-testid={`must-do-swap-${n}`} className="h-10 px-3 rounded-xl border border-[#EFECE6] text-xs font-semibold text-[#635E59] inline-flex items-center gap-1.5"><Shuffle className="h-3.5 w-3.5" />Swap</button>}
        <button disabled={n === 1} onClick={() => move(-1)} aria-label="Move up" data-testid={`must-do-up-${n}`} className={mv}><ArrowUp className="h-4 w-4" /></button>
        <button disabled={n === count} onClick={() => move(1)} aria-label="Move down" data-testid={`must-do-down-${n}`} className={mv}><ArrowDown className="h-4 w-4" /></button>
        {m.status !== "done" && <button disabled={busy} onClick={() => run(() => actMust(m, "drop"), "Dropped from today. No penalty.")} data-testid={`must-do-drop-${n}`} className="h-10 px-3 rounded-xl text-xs font-semibold text-[#8C857B] inline-flex items-center gap-1.5"><X className="h-3.5 w-3.5" />Drop from today</button>}
      </div>
    </motion.div>
  );
}

function MustRow({ m, n, count, onChange, onDragEnd, move }) {
  const controls = useDragControls();
  const [open, setOpen] = useState(false);
  const [helping, setHelping] = useState(false);
  const [busy, setBusy] = useState(false);
  const done = m.status === "done";
  const steps = m.help_steps || [];
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast(msg); await onChange(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Reorder.Item value={m} dragListener={false} dragControls={controls} onDragEnd={onDragEnd} initial={ROW_IN} animate={ROW_SHOWN}
      whileDrag={ROW_DRAG}
      className={`bg-white border rounded-2xl p-4 pl-2 relative overflow-hidden ${done ? "border-[#DCE6DE]" : "border-[#EFECE6]"}`} data-testid={`must-do-item-${n}`}>
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: done ? "#4F6B55" : "#C86D51" }} />
      <div className="flex items-start gap-2">
        <button onPointerDown={(e) => controls.start(e)} aria-label="Drag to reorder" data-testid={`must-do-drag-${n}`} style={{ touchAction: "none" }}
          className="h-11 w-6 shrink-0 grid place-items-center text-[#B8AFA3] cursor-grab active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
        <motion.button whileTap={TAP} disabled={busy} onClick={() => run(() => actMust(m, done ? "undo" : "done"), done ? null : "Done. That counts.")}
          aria-label={done ? "Mark not done" : "Mark done"} data-testid={`must-do-checkbox-${n}`} className="h-11 w-11 -m-1.5 shrink-0 grid place-items-center disabled:opacity-50">
          <motion.span animate={done ? { scale: [1, 1.15, 1] } : {}} className={`h-7 w-7 rounded-lg border-2 grid place-items-center transition-colors ${done ? "bg-[#4F6B55] border-[#4F6B55] text-white" : "border-[#8C857B]"}`}>
            {done && <Check className="h-4 w-4" />}
          </motion.span>
        </motion.button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#C86D51] [&>*]:whitespace-nowrap">
            <span data-testid={`must-do-label-${n}`}>Must {n}</span>
            {m.minutes != null && <span className="font-mono normal-case tracking-normal bg-[#F5EFEB] text-[#635E59] px-1.5 py-0.5 rounded">{m.minutes} min</span>}
            {m.later && m.status !== "done" && <span className="normal-case tracking-normal bg-[#EDE7DD] text-[#635E59] px-1.5 py-0.5 rounded" data-testid={`must-do-later-${n}`}>{m.later === "tomorrow" ? "Moved to tomorrow" : "Let go"}</span>}
            {m.from_yesterday && <span className="normal-case tracking-normal bg-[#FAF4E8] text-[#8A5A16] px-1.5 py-0.5 rounded" data-testid={`must-do-from-yesterday-${n}`}>From yesterday</span>}
            {steps.length > 0 && <button onClick={() => setHelping(true)} className="normal-case tracking-normal font-mono text-[#4F6B55]" data-testid={`must-do-steps-progress-${n}`}>{steps.filter((x) => x.done).length}/{steps.length} steps</button>}
          </div>
          <p className={`text-base font-semibold leading-snug mt-1 ${done ? "line-through decoration-stone-300 text-[#8C857B]" : "text-[#2D2926]"}`} data-testid={`must-do-text-${n}`}>{m.text}</p>
          <div className="mt-2"><LinkChip link={m.link} testid={`must-do-link-${n}`} /></div>
        </div>
        <button onClick={() => setOpen(!open)} aria-label="More" data-testid={`must-do-more-${n}`} className="h-11 w-11 -m-1.5 shrink-0 grid place-items-center text-[#8C857B]">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      <AnimatePresence>{open && <RowMenu m={m} n={n} count={count} busy={busy} run={run} help={() => setHelping(true)} move={move} />}</AnimatePresence>
      {helping && <HelpSheet m={m} open={helping} onClose={() => setHelping(false)} onChange={onChange} />}
    </Reorder.Item>
  );
}

export function MustDos({ board, onChange }) {
  const [items, setItems] = useState(board.must_dos);
  const latest = useRef(items);
  useEffect(() => { setItems(board.must_dos); latest.current = board.must_dos; }, [board.must_dos]);
  const reorder = (next) => { latest.current = next; setItems(next); };
  const save = async (next = latest.current) => {
    if (next.map((x) => x.id).join() === board.must_dos.map((x) => x.id).join()) return;
    try { await api.post("/v2/today/must-order", { ids: next.map((x) => x.id) }); await onChange(); } catch (e) { toast.error(errMsg(e)); setItems(board.must_dos); }
  };
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; reorder(a); save(a); };
  return (
    <section data-testid="must-dos">
      <div className="px-1 mb-3">
        <h2 className="font-display text-2xl tracking-tight text-[#2D2926]">Today's must-dos</h2>
        {board.commitment && <p className="text-xs text-[#8C857B] mt-1" data-testid="must-dos-commitment">{board.commitment}</p>}
      </div>
      {items.length === 0
        ? <p className="text-sm text-[#635E59] px-1" data-testid="must-dos-empty">Everything's been dropped for today. That's allowed. Add a to-do below if you want one.</p>
        : <Reorder.Group axis="y" values={items} onReorder={reorder} as="ul" className="space-y-2.5" data-testid="must-dos-list">
          {items.map((m, i) => <MustRow key={m.id} m={m} n={i + 1} count={items.length} onChange={onChange} onDragEnd={() => save()} move={(d) => move(i, d)} />)}
        </Reorder.Group>}
    </section>
  );
}
